import React, { useState, useEffect, useCallback } from 'react';
import NoteTemplatesTable from './NoteTemplatesTable';
import Button from '../../BaseComponents/Button';
import NoteTemplatesModal from './NoteTemplatesModal';
import ViewNoteTemplateModal from './NoteTemplatesModal/ViewNoteTemplateModal';
import DeleteNoteTemplateModal from './NoteTemplatesModal/DeleteNoteTemplateModal';
import { NoteTemplate, getNoteTemplatesList } from '../../../api/note-templates';

import './NoteTemplatesTab.scss';

const NoteTemplatesTab = () => {
    const [templatesList, setTemplatesList] = useState<NoteTemplate[]>([]);
    const [offset, setOffset] = useState<number>(0);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [templateToView, setTemplateToView] = useState<NoteTemplate | null>(null);
    const [templateToEdit, setTemplateToEdit] = useState<NoteTemplate | null>(null);
    const [templateToDelete, setTemplateToDelete] = useState<NoteTemplate | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const limit = 10;

    const fetchTemplatesList = useCallback(async (newOffset: number = 0, append: boolean = false) => {
        if (append) {
            setIsLoadingMore(true);
        } else {
            setIsLoading(true);
        }

        try {
            const res = await getNoteTemplatesList(undefined, limit, newOffset);
            const newTemplates = res?.note_templates || [];
            const total = typeof res?.total_count === 'number'
                ? res.total_count
                : Array.isArray(newTemplates)
                    ? newTemplates.length
                    : 0;

            setTemplatesList((prev) =>
                append ? [...prev, ...newTemplates] : newTemplates
            );

            const nextOffset = newOffset + limit;
            setOffset(nextOffset);

            if (total > 0) {
                setHasMore(nextOffset < total && newTemplates.length === limit);
            } else {
                setHasMore(newTemplates.length === limit);
            }
        } catch (error) {
            console.error('Error fetching note templates:', error);
        } finally {
            if (append) {
                setIsLoadingMore(false);
            } else {
                setIsLoading(false);
            }
        }
    }, [limit]);

    useEffect(() => {
        
        fetchTemplatesList(0, false);
    }, [fetchTemplatesList]);

    const handleLoadMore = useCallback(() => {
        if (isLoading || isLoadingMore || !hasMore) return;
        fetchTemplatesList(offset, true);
    }, [fetchTemplatesList, offset, isLoading, isLoadingMore, hasMore]);

    const onAddTemplateBtnClick = () => {
        setTemplateToEdit(null);
        setIsFormModalOpen(true);
    };

    const onTemplateSaved = (data: any) => {
        setIsFormModalOpen(false);
        setTemplateToEdit(null);
        
        fetchTemplatesList(0, false);
    };

    const handleView = (template: NoteTemplate) => {
        setTemplateToView(template);
        setIsViewModalOpen(true);
    };

    const handleEdit = (template: NoteTemplate) => {
        setTemplateToEdit(template);
        setIsFormModalOpen(true);
    };

    const handleDelete = (template: NoteTemplate) => {
        setTemplateToDelete(template);
        setIsDeleteModalOpen(true);
    };

    return (
        <>
            <div className='search-row-container'>
                <div className='search-and-filter-container'>
                    <div className='count-container'>
                        {templatesList.length === 1 ? 'Note Template ( ' + templatesList.length + ' )' : 'Note Templates ( ' + templatesList.length + ' )'}
                    </div>
                </div>
                <Button
                    isSecondary={true}
                    text='Add Note Template'
                    onClick={onAddTemplateBtnClick}
                />
            </div>

            <div className='panels-container'>
                {isLoading ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>Loading templates...</div>
                ) : (
                    <NoteTemplatesTable
                        templatesList={templatesList}
                        onView={handleView}
                        hasMore={hasMore}
                        isLoadingMore={isLoadingMore}
                        onLoadMore={handleLoadMore}
                    />
                )}
            </div>

            {isViewModalOpen && (
                <ViewNoteTemplateModal
                    isOpen={isViewModalOpen}
                    onClose={() => {
                        setIsViewModalOpen(false);
                        setTemplateToView(null);
                    }}
                    template={templateToView}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                />
            )}

            {isFormModalOpen && (
                <NoteTemplatesModal
                    isOpen={isFormModalOpen}
                    onClose={() => {
                        setIsFormModalOpen(false);
                        setTemplateToEdit(null);
                    }}
                    onSaved={onTemplateSaved}
                    templateToEdit={templateToEdit}
                />
            )}


            <DeleteNoteTemplateModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setTemplateToDelete(null);
                }}
                onDeleted={() => {
                    fetchTemplatesList();
                    setIsViewModalOpen(false);
                    setTemplateToView(null);
                }}
                templateToDelete={templateToDelete}
            />

        </>
    );
};

export default NoteTemplatesTab;
